import cloneDeep from "lodash.clonedeep";
import { copyPage } from "../property/structure";

// The pages of an experiment: the ones a variant can point to, and the
// ones the Save creates (a variant marked `created` whose page does not
// exist yet).

// The item of a Save that sends rows besides the experiment (saveExtra of the
// Item), in the format the draft store collects: a new row (isNew, baseVersion
// 0), or a stored one with the version it was read at.
export const newItem = (accessor, row) => ({
  accessor,
  Id: row.id,
  prevId: row.id,
  isNew: true,
  baseVersion: 0,
  data: row,
  notify: false
});

export const editItem = (accessor, row) => ({
  accessor,
  Id: row.id,
  prevId: row.id,
  isNew: false,
  baseVersion: Number(row.version ?? 0),
  data: row,
  notify: false
});

// The pages (ids of blocks) variant `index` can point to: the inactive
// variants of the endpoint that no other variant uses. The control is
// not one of them: its page is the default one of the endpoint.
export function selectablePages(endpoint, variations = [], index) {
  const taken = new Set(
    variations
      .filter((variation, i) => i !== index)
      .map((variation) => variation?.blockId)
      .filter(Boolean)
  );

  return (endpoint?.variants ?? [])
    .filter(
      (variant) =>
        variant?.blockId && !variant.active && !taken.has(variant.blockId)
    )
    .map((variant) => variant.blockId);
}

// A variant whose page will be created by the Save: marked created, and
// there is no page with its id yet
export const isPendingPage = (variation, blockExists) =>
  Boolean(
    variation?.created &&
      variation.blockId &&
      !variation.control &&
      !blockExists(variation.blockId)
  );

export const newPageTitle = (experiment, variation) =>
  `${experiment?.title || experiment?.id} - ${variation?.label || variation?.value}`;

// The rows the Save of the experiment sends to create its pending pages, in
// the order the server wants them: the new blocks first (the endpoint
// requires them), then the endpoint with the new variants and the property
// with the new nodes in its tree. Each page is a copy of the default page of
// the endpoint, built by copyPage (property/structure.js) as the property
// builds it, with its ops applied to the rows given by `peek` (changed on
// copies, never in place), one after the other: the second page is added to
// the endpoint and the tree that already have the first. Nothing to send:
// [].
// - endpoint: the stored row of the endpoint of the experiment;
// - peek(accessor, id), stored(accessor, id): the stored row, or undefined
//   (see structure.js);
// - doRefs: the refs of an endpoint (doEndpointRefs);
// - idGen: the id of the page, the one its variant already has.
export function createdPagesItems({
  experiment,
  endpoint,
  peek,
  stored,
  doRefs
}) {
  const pending = (experiment?.variations ?? []).filter((variation) =>
    isPendingPage(variation, (id) => peek("blocks", id) !== undefined)
  );

  if (pending.length === 0) return [];
  if (!endpoint)
    throw new Error("The endpoint of the experiment doesn't exist anymore");
  if (!endpoint.propertyId)
    throw new Error(
      `The endpoint ${endpoint.reference} is in no property: its pages can't be created`
    );

  const created = [];
  const edited = new Map();
  const peekEdited = (accessor, id) =>
    edited.get(`${accessor}:${id}`)?.row ?? peek(accessor, id);

  for (const variation of pending) {
    const { ops } = copyPage({
      propertyId: endpoint.propertyId,
      blockId: endpoint.blockId,
      destEndpointId: endpoint.id,
      title: newPageTitle(experiment, variation),
      peek: peekEdited,
      stored,
      doRefs,
      idGen: () => variation.blockId,
      treeVia: "op"
    });

    for (const op of ops) {
      if (op.kind === "create") created.push(op.row);
      else
        edited.set(`${op.accessor}:${op.id}`, {
          accessor: op.accessor,
          row: op.updater(cloneDeep(peekEdited(op.accessor, op.id)))
        });
    }
  }

  return [
    ...created.map((row) => newItem("blocks", row)),
    ...[...edited.values()].map(({ accessor, row }) => editItem(accessor, row))
  ];
}
